import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountService from "../../account/AccountService";
import Logger from "../../../services/Logger";
import UserService from "../../../services/UserService";
import WebPushService from "../../../services/WebPushService";

const DEFAULT_TITLE = "Test notification";
const DEFAULT_BODY = "This is a test from Puzzle Interact on this PC.";

function uniqueWebTokenIds(tokens) {
    const seen = new Set();
    const ids = [];
    for (const token of tokens) {
        if (token?.platform !== "web" || !token?.id) {
            continue;
        }
        const key = token.deviceTokenPreview || token.id;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        ids.push(token.id);
    }
    return ids;
}

export default function SendNotificationMain() {
    const navigate = useNavigate();
    const [title, setTitle] = useState(DEFAULT_TITLE);
    const [body, setBody] = useState(DEFAULT_BODY);
    const [cloudUserId, setCloudUserId] = useState("");
    const [loadingUser, setLoadingUser] = useState(true);
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState({ severity: "info", message: "" });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const userInfo = await UserService.getUserInfo();
                const id = userInfo?.cloudUser?.id || "";
                if (!cancelled) {
                    setCloudUserId(id);
                    if (!id) {
                        setStatus({
                            severity: "warning",
                            message: "Could not load your user id. Sign in again and retry.",
                        });
                    }
                }
            } catch (error) {
                Logger.error("SendNotificationMain load user:", error);
                if (!cancelled) {
                    setStatus({
                        severity: "error",
                        message: error.message || "Failed to load the signed-in user.",
                    });
                }
            } finally {
                if (!cancelled) {
                    setLoadingUser(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSend = async () => {
        if (!cloudUserId) {
            setStatus({ severity: "warning", message: "Sign in is required." });
            return;
        }
        const message = body.trim();
        if (!message) {
            setStatus({ severity: "warning", message: "Enter a message." });
            return;
        }
        setSending(true);
        setStatus({ severity: "info", message: "" });
        try {
            const subscribe = await WebPushService.syncFromPreference(true, {
                requestPermission: true,
            });
            if (!subscribe.ok) {
                const reason = subscribe.reason;
                if (reason === "denied") {
                    setStatus({
                        severity: "error",
                        message: "Notifications are blocked in this browser. Allow them for localhost, then try again.",
                    });
                    return;
                }
                if (reason === "unsupported") {
                    setStatus({
                        severity: "error",
                        message: "This browser does not support Web Push.",
                    });
                    return;
                }
                setStatus({
                    severity: "warning",
                    message: `Could not subscribe this browser (${reason}). Allow notifications and try again.`,
                });
                return;
            }

            const listed = await AccountService.listPushDeviceTokens(cloudUserId);
            const tokens = Array.isArray(listed?.content) ? listed.content : [];
            const webIds = uniqueWebTokenIds(tokens);
            if (webIds.length === 0) {
                setStatus({
                    severity: "warning",
                    message: "This PC is not registered for web push yet. Allow notifications, then send again.",
                });
                return;
            }

            const result = await AccountService.sendTestNotification(cloudUserId, {
                deviceTokenIds: webIds,
                title: title.trim() || DEFAULT_TITLE,
                body: message,
            });
            const sent = Number(result?.sent) || 0;
            if (sent > 0) {
                setStatus({
                    severity: "success",
                    message: `Sent to this PC (${sent} web subscription${sent === 1 ? "" : "s"}). Check the browser notification.`,
                });
            } else {
                setStatus({
                    severity: "warning",
                    message: result?.message || "Nothing was sent. Restart the backend if it is not running.",
                });
            }
        } catch (error) {
            Logger.error("SendNotificationMain send:", error);
            setStatus({
                severity: "error",
                message: error.message || "Failed to send. Is the backend running on localhost:8080?",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ p: 2, maxWidth: 640, mx: "auto" }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/admin/system/anything")}
                sx={{ mb: 2, minHeight: 44, textTransform: "none" }}
            >
                Anything
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Send notification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sends only to your signed-in account on this PC (web push). It does not broadcast
                to other users or to phones.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                {loadingUser ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <TextField
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Message"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            minRows={4}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendOutlinedIcon />}
                            onClick={handleSend}
                            disabled={sending || !cloudUserId}
                            sx={{ minHeight: 44 }}
                        >
                            {sending ? "Sending…" : "Send"}
                        </Button>
                    </>
                )}
            </Paper>
            {status.message ? (
                <Alert severity={status.severity} sx={{ mt: 2 }}>
                    {status.message}
                </Alert>
            ) : null}
        </Box>
    );
}
